import { Injectable, computed, inject, signal } from '@angular/core';
import { Table } from 'dexie';

import { ConnectivityService } from '../connectivity.service';
import { AppDatabase, DatabaseService } from '../database.service';
import {
  SyncPermanentError,
  SyncTransientError,
  isQuotaError,
} from '../errors/offline.errors';
import {
  PULLABLE_ENTITIES,
  PUSHABLE_ENTITIES,
  SYNC_REGISTRY,
  describe,
} from '../models/sync-registry';
import {
  ConflictResolution,
  EntityName,
  HydrationStage,
  HydrationState,
  SyncOperation,
  SyncState,
  SyncableEntity,
} from '../models/sync.model';
import { StorageService } from '../storage.service';
import {
  SyncApiService,
  SyncChangeRecord,
  SyncPushOperation,
  SyncPushResult,
} from './sync-api.service';
import { SyncQueueRepository } from './sync-queue.repository';

/**
 * The login-time load, grouped into stages a teacher recognises.
 *
 * Entity order inside and across stages follows PULLABLE_ENTITIES, because it is
 * dependency order: sections before the items in them, items before the
 * questions they point at. Reordering these for presentation would mean a child
 * arriving with no parent to attach to.
 */
const HYDRATION_STAGES: ReadonlyArray<
  Pick<HydrationStage, 'key' | 'labelKey' | 'label' | 'entities'>
> = [
  {
    key: 'people',
    labelKey: 'offline.loadingClasses',
    label: 'Loading your classes and students',
    entities: ['classrooms', 'students'],
  },
  {
    key: 'assessments',
    labelKey: 'offline.loadingAssessments',
    label: 'Loading assessments',
    entities: ['assessments', 'assessmentSections', 'sectionItems'],
  },
  {
    key: 'questions',
    labelKey: 'offline.loadingQuestions',
    label: 'Loading questions and options',
    entities: ['questions', 'questionOptions'],
  },
  {
    key: 'work',
    labelKey: 'offline.loadingWork',
    label: 'Loading saved work and reports',
    entities: [
      'assignmentIntervals',
      'assessmentAttempts',
      'assessmentResults',
      'selectedOptions',
      'reports',
    ],
  },
];

/**
 * Drives synchronisation between IndexedDB and the server.
 *
 * Shape of one cycle:
 *
 *   push  local changes  ->  server assigns ids/versions  ->  apply results
 *   pull  server changes ->  upsert locally, skipping rows with local work
 *
 * Push runs first, always. Pulling first would fetch rows the client is about
 * to change anyway, and would make a locally edited record look like a conflict
 * against a server state the client had not yet had the chance to update.
 *
 * The service is never called by components to fetch data - they read
 * repositories. It runs in the background and its only visible surface is
 * `state`, which the UI renders as a status badge.
 */
@Injectable({ providedIn: 'root' })
export class SyncService {
  private readonly databaseService = inject(DatabaseService);
  private readonly connectivity = inject(ConnectivityService);
  private readonly queue = inject(SyncQueueRepository);
  private readonly api = inject(SyncApiService);
  private readonly storage = inject(StorageService);

  /** Operations per request. Small enough to fit a slow uplink's timeout. */
  private static readonly PUSH_BATCH_SIZE = 50;
  /** Records per pull page. */
  private static readonly PULL_PAGE_SIZE = 500;
  /** Safety net so a missed reconnect event cannot strand the queue. */
  private static readonly PERIODIC_SYNC_MS = 2 * 60_000;
  /**
   * How often to look for a session while nobody is signed in.
   *
   * Much shorter than the periodic sync: this is the gap between a teacher
   * finishing login and their data appearing, so minutes would read as "the app
   * is broken". It costs a localStorage read and stops the moment a token shows.
   */
  private static readonly AUTH_POLL_MS = 3_000;
  /**
   * How long the finished overlay stays up.
   *
   * On a second login the whole load takes a few hundred milliseconds, and
   * without this the overlay appears and vanishes as a flicker - which reads as
   * a glitch rather than as work completing.
   */
  private static readonly HYDRATION_LINGER_MS = 900;

  private readonly stateSignal = signal<SyncState>({
    phase: 'idle',
    pendingCount: 0,
    failedCount: 0,
    conflictCount: 0,
    lastSyncedAt: null,
    lastError: null,
  });

  private readonly hydrationSignal = signal<HydrationState>({
    active: false,
    stages: [],
    completedStages: 0,
    totalStages: 0,
    totalRecords: 0,
    error: null,
    finished: false,
  });

  readonly state = this.stateSignal.asReadonly();

  /** Progress of the login-time load, rendered by the hydration overlay. */
  readonly hydration = this.hydrationSignal.asReadonly();

  /** 0-100, for the progress bar. */
  readonly hydrationPercent = computed(() => {
    const h = this.hydrationSignal();
    if (h.totalStages === 0) return 0;
    return Math.round((h.completedStages / h.totalStages) * 100);
  });
  readonly isSyncing = computed(() => this.stateSignal().phase === 'syncing');
  readonly hasConflicts = computed(() => this.stateSignal().conflictCount > 0);
  readonly hasPendingWork = computed(() => {
    const s = this.stateSignal();
    return s.pendingCount > 0 || s.failedCount > 0;
  });

  /** Additional tasks that run after the table-pulling stages. */
  private readonly hydrationTasks: Array<{
    key: string;
    labelKey: string;
    label: string;
    run: (report: (records: number) => void) => Promise<number>;
  }> = [];

  /**
   * Register a task to be run during login-time hydration.
   *
   * Called during application bootstrap so the task is present before the first
   * sync happens.
   */
  registerHydrationTask(task: {
    key: string;
    labelKey: string;
    label: string;
    run: (report: (records: number) => void) => Promise<number>;
  }): void {
    this.hydrationTasks.push(task);
  }

  /** Guards against two cycles overlapping. */
  private running = false;
  /** Synchronous re-entrancy guard for hydrate(); see the note there. */
  private hydrating = false;
  private started = false;
  private timer: ReturnType<typeof setInterval> | null = null;
  private authTimer: ReturnType<typeof setInterval> | null = null;

  /**
   * Begin background synchronisation. Idempotent, so it is safe to call from an
   * APP_INITIALIZER and again after login.
   */
  async start(): Promise<void> {
    if (this.started || !this.databaseService.isAvailable) return;
    this.started = true;

    // Operations left mid-flight by a closed tab. Safe to replay: the server
    // dedupes on opId.
    await this.queue.recoverStuck().catch(() => 0);
    await this.storage.requestPersistence().catch(() => false);
    await this.refreshCounts();

    // The reconnect edge is the trigger that matters - this is what makes sync
    // automatic and removes any need for the user to ask for it.
    this.connectivity.reconnected$.subscribe(() => void this.sync('reconnect'));
    this.timer = setInterval(
      () => void this.sync('interval'),
      SyncService.PERIODIC_SYNC_MS,
    );

    // APP_INITIALIZER runs before the login screen, so on a fresh session there
    // is no token yet. Syncing anyway earns a 401, which is a *permanent*
    // failure - correct in general, badly wrong here, because the very next
    // thing that happens is the user signing in. So wait for the session
    // instead of failing into an error state nothing clears.
    if (this.api.hasAuthToken()) {
      if (this.connectivity.isOnline()) void this.sync('startup');
    } else {
      this.watchForAuth();
    }
  }

  /**
   * Call immediately after a successful sign-in.
   *
   * The poll below would pick it up within a few seconds anyway; this makes it
   * instant, which is what the teacher experiences as "my data is here".
   */
  onAuthenticated(): void {
    this.stopAuthWatch();
    // The visible load, not the silent one: this is the moment the teacher is
    // waiting on an empty app.
    void this.hydrate();
  }

  /** Watch for a session appearing, then sync once and stop watching. */
  private watchForAuth(): void {
    if (this.authTimer || !this.databaseService.isAvailable) return;
    this.authTimer = setInterval(() => {
      if (!this.api.hasAuthToken()) return;
      this.stopAuthWatch();
      // A session appeared without onAuthenticated() being called - a login
      // path that does not know about sync, or another tab. Still the first
      // load for this teacher, so show it rather than filling silently.
      void this.hydrate();
    }, SyncService.AUTH_POLL_MS);
  }

  private stopAuthWatch(): void {
    if (this.authTimer) clearInterval(this.authTimer);
    this.authTimer = null;
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.stopAuthWatch();
    this.started = false;
  }

  /**
   * Run one full cycle.
   *
   * Returns quietly when offline or already running - callers may fire this
   * freely (after a save, on a button, on a timer) without coordinating.
   */
  async sync(reason: 'startup' | 'reconnect' | 'interval' | 'manual' = 'manual'): Promise<void> {
    if (this.running) return;
    if (!this.databaseService.isAvailable) return;

    // Nobody signed in: there is nothing to sync and no credential to sync it
    // with. Skip quietly - this is a normal state, not a failure - and keep
    // watching so the first sync happens as soon as they log in.
    if (!this.api.hasAuthToken()) {
      this.watchForAuth();
      this.patch({ phase: 'idle', lastError: null });
      return;
    }

    if (!this.connectivity.isOnline()) {
      // Confirm before giving up: the flag may simply be stale.
      const online = await this.connectivity.check();
      if (!online) {
        this.patch({ phase: 'offline' });
        return;
      }
    }

    this.running = true;
    this.patch({ phase: 'syncing', lastError: null });

    try {
      await this.pushAll();
      await this.pullAll();
      await this.queue.setLastSyncedAt(Date.now());
      this.patch({
        phase: 'idle',
        lastSyncedAt: await this.queue.getLastSyncedAt(),
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      // A permanent failure (expired session, unusable payload) must not look
      // like a transient one: the badge shows an error and the loop stops
      // hammering the endpoint until something changes.
      this.patch({
        phase: error instanceof SyncPermanentError ? 'error' : 'idle',
        lastError: message,
      });
      if (reason === 'manual') throw error;
    } finally {
      this.running = false;
      await this.refreshCounts();
    }
  }

  // ── Push ─────────────────────────────────────────────────────────────────

  /** Send queued work until nothing is due. */
  private async pushAll(): Promise<void> {
    for (;;) {
      const batch = await this.queue.takeDue(SyncService.PUSH_BATCH_SIZE);
      if (!batch.length) return;

      const sent = await this.pushBatch(batch);
      // Nothing moved: everything in the batch was deferred or failed, so
      // looping again would spin on the same rows.
      if (!sent) return;
    }
  }

  /** Push one batch. Returns false when nothing could be sent. */
  private async pushBatch(batch: SyncOperation[]): Promise<boolean> {
    const db = await this.databaseService.ready();

    // An answer whose attempt has not synced yet cannot be sent: the server has
    // no row to attach it to. Resolve the parent's id where it now exists, and
    // defer the rest until the parent's own create has landed.
    const sendable: SyncOperation[] = [];
    const deferred: SyncOperation[] = [];
    for (const op of batch) {
      const prepared = await this.resolveParentIds(db, op);
      (prepared ? sendable : deferred).push(prepared ?? op);
    }
    for (const op of deferred) await this.queue.release(op);
    if (!sendable.length) return false;

    await this.queue.markSyncing(sendable);

    const wire: SyncPushOperation[] = sendable.map((op) => ({
      opId: op.opId,
      entity: op.entity,
      operation: op.operation,
      uuid: op.uuid,
      serverId: op.serverId,
      baseVersion: op.baseVersion,
      payload: op.payload,
      clientUpdatedAt: op.updatedAt,
    }));

    let results: SyncPushResult[];
    try {
      results = (await this.api.push(wire)).results;
    } catch (error) {
      // The whole request failed, which says nothing about any individual
      // operation. Transient: put them back untouched so an outage does not
      // burn their retry budget. Permanent: count it against them and stop.
      if (error instanceof SyncTransientError) {
        for (const op of sendable) await this.queue.release(op);
        throw error;
      }
      const message = error instanceof Error ? error.message : String(error);
      for (const op of sendable) await this.queue.markFailed(op, message);
      throw error;
    }

    const byOpId = new Map(sendable.map((op) => [op.opId, op]));
    for (const result of results) {
      const op = byOpId.get(result.opId);
      if (op) await this.applyResult(op, result);
    }

    // Anything the server did not answer for stays queued rather than being
    // assumed successful.
    const answered = new Set(results.map((r) => r.opId));
    for (const op of sendable) {
      if (!answered.has(op.opId)) await this.queue.release(op);
    }
    return true;
  }

  /**
   * Fill in a parent's server id, or report that it is not available yet.
   *
   * Returns the operation ready to send, or null when the parent is still
   * unsynced and the child must wait.
   */
  private async resolveParentIds(
    db: AppDatabase,
    op: SyncOperation,
  ): Promise<SyncOperation | null> {
    const parent = describe(op.entity).parent;
    if (!parent || !op.payload) return op;

    const parentUuid = op.payload[parent.uuidField];
    if (typeof parentUuid !== 'string' || !parentUuid) return op;

    // Already carries a real parent id.
    const current = op.payload[parent.serverField];
    if (typeof current === 'number' && current > 0) return op;

    const parentTable = db.table<SyncableEntity, number>(
      SYNC_REGISTRY[parent.entity].table,
    );
    const parentRow = await parentTable.where('uuid').equals(parentUuid).first();
    if (!parentRow || parentRow.serverId === null) return null;

    return {
      ...op,
      payload: { ...op.payload, [parent.serverField]: parentRow.serverId },
    };
  }

  /** Apply one per-operation outcome to the local store. */
  private async applyResult(op: SyncOperation, result: SyncPushResult): Promise<void> {
    const db = await this.databaseService.ready();
    const table = db.table<SyncableEntity, number>(describe(op.entity).table);

    switch (result.status) {
      case 'applied':
      case 'skipped': {
        // `skipped` means the server had already applied this exact opId - a
        // replay after a lost response. Same local outcome as `applied`; that
        // is what makes retries safe.
        await db.transaction('rw', table, db.syncQueue, async () => {
          const row = await table.get(op.localId);
          if (row) {
            if (op.operation === 'delete') {
              // Confirmed server-side: the tombstone has done its job.
              await table.delete(op.localId);
            } else {
              // Adopt the server's uuid when it differs from ours - see
              // SyncPushResult.uuid. Without this the two sides keep separate
              // identities for one record and the next pull inserts a duplicate.
              await table.put({
                ...row,
                uuid: result.uuid ?? row.uuid,
                serverId: result.serverId ?? row.serverId,
                version: result.version ?? row.version,
                syncStatus: 'synced',
              });
            }
          }
          await db.syncQueue.delete(op.id as number);
        });
        return;
      }

      case 'conflict': {
        await this.recordConflict(op, result);
        return;
      }

      case 'rejected': {
        // The server will not accept this operation as-is. Retrying unchanged
        // cannot help, so it is parked at the retry ceiling for a human.
        await this.queue.markFailed(
          { ...op, attempts: Number.MAX_SAFE_INTEGER - 1 },
          result.message ?? 'Rejected by server',
        );
        return;
      }
    }
  }

  /**
   * Record a divergence without resolving it.
   *
   * Both versions are preserved and the record is frozen in `conflict` until
   * something decides. The queued operation is removed so the loop stops
   * retrying a write the server will keep refusing - the decision, not another
   * attempt, is what unblocks it.
   */
  private async recordConflict(op: SyncOperation, result: SyncPushResult): Promise<void> {
    const db = await this.databaseService.ready();
    const table = db.table<SyncableEntity, number>(describe(op.entity).table);
    const local = await table.get(op.localId);

    await this.queue.recordConflict({
      entity: op.entity,
      localId: op.localId,
      uuid: op.uuid,
      serverId: result.serverId ?? op.serverId,
      localSnapshot: (op.payload ?? local ?? {}) as Record<string, unknown>,
      serverSnapshot: result.serverRecord ?? {},
      baseVersion: op.baseVersion,
      serverVersion: result.serverVersion ?? 0,
      detectedAt: Date.now(),
      resolvedAt: null,
      resolution: null,
    });

    if (local) {
      await table.put({ ...local, syncStatus: 'conflict' });
    }
    await this.queue.removeForRecord(op.entity, op.localId);
  }

  // ── Pull ─────────────────────────────────────────────────────────────────

  /** Refresh every pullable entity from its cursor. */
  private async pullAll(): Promise<void> {
    for (const entity of PULLABLE_ENTITIES) {
      try {
        await this.pullEntity(entity);
      } catch (error) {
        if (isQuotaError(error)) {
          // Out of room. Stop pulling - more data is exactly what we cannot
          // take - but leave already-pushed work intact.
          this.patch({
            lastError:
              'Local storage is full. Free space or clear cached data to keep syncing.',
          });
          return;
        }
        if (error instanceof SyncPermanentError) throw error;
        // One entity failing should not abort the rest of the cycle.
      }
    }
  }

  /** Pull one entity to exhaustion. Returns how many records were applied. */
  private async pullEntity(entity: EntityName): Promise<number> {
    const db = await this.databaseService.ready();
    const table = db.table<SyncableEntity, number>(describe(entity).table);

    let cursor = await this.queue.getCursor(entity);
    let applied = 0;

    for (let page = 0; page < 50; page++) {
      const response = await this.api.changes(entity, cursor, SyncService.PULL_PAGE_SIZE);
      if (response.records.length) {
        await this.applyServerRecords(db, table, response.records);
        applied += response.records.length;
      }

      cursor = response.nextCursor;
      await this.queue.setCursor(entity, cursor);
      if (!response.hasMore) break;
    }

    return applied;
  }

  // ── Login-time hydration ─────────────────────────────────────────────────

  /**
   * Load everything this teacher needs, reporting progress as it goes.
   *
   * Distinct from `sync()` in one way that matters: it is *visible*. A silent
   * background sync is right for every later top-up, but on the first login the
   * teacher is looking at an empty app and has to be told it is filling, or the
   * wait reads as a broken screen.
   *
   * Incremental all the same - it pulls from the stored cursors - so a second
   * login finishes almost instantly and the overlay barely appears.
   *
   * A failing stage does not abort the rest. Partial data beats none: a teacher
   * whose reports failed to load can still grade.
   */
  async hydrate(): Promise<void> {
    if (!this.databaseService.isAvailable || !this.api.hasAuthToken()) return;

    // Guard synchronously, before the first await.
    //
    // The `active` flag cannot do this job: it is set several awaits later, so
    // a second call arriving in that window sails past it and runs a duplicate
    // load - double the requests, and stage counters that climb past 100%.
    // Two callers is the normal case, not an edge one: login calls
    // onAuthenticated() at the same moment the auth watcher notices the token.
    if (this.hydrating) return;
    this.hydrating = true;

    // Before anything else: is this the same teacher as last time?
    await this.ensureUserScope();

    // The table stages, then any registered tasks - they run after, because they
    // build on the rows the table stages just stored.
    const stages: HydrationStage[] = [
      ...HYDRATION_STAGES,
      ...this.hydrationTasks.map(({ key, labelKey, label }) => ({
        key,
        labelKey,
        label,
        entities: [] as readonly EntityName[],
      })),
    ].map((stage) => ({
      ...stage,
      status: 'pending' as const,
      records: 0,
    }));

    this.hydrationSignal.set({
      active: true,
      stages,
      completedStages: 0,
      totalStages: stages.length,
      totalRecords: 0,
      error: null,
      finished: false,
    });

    try {
      // Anything recorded offline goes up before new data comes down, so a
      // pending edit is never overwritten by the server copy it was based on.
      if (this.connectivity.isOnline()) {
        await this.pushAll().catch(() => undefined);
      }

      for (let index = 0; index < stages.length; index++) {
        this.patchStage(index, { status: 'loading' });

        let records = 0;
        let failed = false;

        const task = this.hydrationTasks.find((t) => t.key === stages[index].key);
        if (task) {
          try {
            records = await task.run((count) => this.patchStage(index, { records: count }));
          } catch (error) {
            failed = true;
            const message = error instanceof Error ? error.message : String(error);
            this.hydrationSignal.update((s) => ({ ...s, error: message }));
          }
        }

        for (const entity of stages[index].entities) {
          try {
            records += await this.pullEntity(entity);
          } catch (error) {
            failed = true;
            const message = error instanceof Error ? error.message : String(error);
            this.hydrationSignal.update((s) => ({ ...s, error: message }));
            if (error instanceof SyncPermanentError) throw error;
          }
        }

        this.patchStage(index, {
          status: failed ? 'failed' : 'done',
          records,
        });
        this.hydrationSignal.update((s) => ({
          ...s,
          completedStages: s.completedStages + 1,
          totalRecords: s.totalRecords + records,
        }));
      }

      await this.queue.setLastSyncedAt(Date.now());
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.hydrationSignal.update((s) => ({ ...s, error: message }));
    } finally {
      this.hydrating = false;
      this.hydrationSignal.update((s) => ({ ...s, finished: true }));
      await this.refreshCounts();

      // Hold the finished state briefly so the last tick is visible rather than
      // flashing past - the difference between "it loaded" and "something
      // blinked at me".
      setTimeout(() => {
        this.hydrationSignal.update((s) => ({ ...s, active: false }));
      }, SyncService.HYDRATION_LINGER_MS);
    }
  }

  /**
   * Make sure the local database belongs to the teacher who is signed in.
   *
   * Devices are shared. Two teachers on one tablet is the normal case in a
   * school, and without this the second one is handed the first one's data:
   * the pull cursors are already advanced, so their own hydration fetches
   * nothing and they see someone else's classes - or an empty app.
   *
   * On a change of user everything local is dropped and hydration starts from
   * scratch. Safe because the previous teacher's work is pushed before this
   * point (hydrate pushes first), and anything still queued belonged to a
   * session that has ended.
   */
  private async ensureUserScope(): Promise<void> {
    const current = this.api.currentUserKey();
    if (!current) return;

    const stored = await this.queue.getUserKey();
    if (stored === current) return;

    if (stored !== null) {
      // A different teacher. Clear the previous one's data rather than layering
      // this one's on top of it.
      await this.resetLocalData();
    }
    await this.queue.setUserKey(current);
  }

  /** Coalesces a burst of online writes (auto-save) into one pull. */
  private static readonly WRITE_SYNC_DEBOUNCE_MS = 2_000;
  private backgroundSyncTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Bring the local tables in step shortly after a write reached the server.
   *
   * An online save goes to the API, and the server derives more than the row
   * that was sent - the score, attempt progress, section roll-ups. Pulling
   * afterwards is what puts those into IndexedDB with their real ids and
   * versions, so the next offline edit starts from the server's truth.
   */
  requestBackgroundSync(): void {
    if (!this.databaseService.isAvailable) return;
    if (this.backgroundSyncTimer) clearTimeout(this.backgroundSyncTimer);
    this.backgroundSyncTimer = setTimeout(() => {
      this.backgroundSyncTimer = null;
      // A cycle already running may have pulled before this write landed; try
      // again once it finishes rather than dropping the request.
      if (this.running) {
        this.requestBackgroundSync();
        return;
      }
      void this.sync('interval');
    }, SyncService.WRITE_SYNC_DEBOUNCE_MS);
  }

  /**
   * Empty every synced table and forget every cursor.
   *
   * Used on a change of user, and available for logout. The queue and conflicts
   * go too: they describe work by a teacher who is no longer here. So do the
   * cached responses, which hold the previous teacher's screens.
   *
   * The outbox is NOT cleared. It holds writes that have not reached the server
   * at all, and each entry is only ever replayed under its own teacher's session
   * (OutboxService filters by userKey), so keeping it loses nothing and exposes
   * nothing.
   */
  async resetLocalData(): Promise<void> {
    if (!this.databaseService.isAvailable) return;
    const db = await this.databaseService.ready();

    const tables = [
      ...PULLABLE_ENTITIES.map((entity) => describe(entity).table),
      'syncQueue',
      'conflicts',
      'syncMeta',
      'httpCache',
    ];

    await db.transaction(
      'rw',
      tables.map((name) => db.table(name)),
      async () => {
        for (const name of tables) await db.table(name).clear();
      },
    );

    this.patch({
      pendingCount: 0,
      failedCount: 0,
      conflictCount: 0,
      lastSyncedAt: null,
      lastError: null,
    });
  }

  private patchStage(index: number, patch: Partial<HydrationStage>): void {
    this.hydrationSignal.update((s) => {
      const stages = [...s.stages];
      stages[index] = { ...stages[index], ...patch };
      return { ...s, stages };
    });
  }

  /**
   * Merge server rows into the local table.
   *
   * The rule that protects offline work: a local row that is not `synced` is
   * never overwritten. Either it is queued (its own push will settle it) or it
   * is already in conflict (a decision is outstanding). Blindly writing the
   * server's copy over either would silently discard the teacher's edit - the
   * exact failure this design exists to prevent.
   */
  private async applyServerRecords(
    db: AppDatabase,
    table: Table<SyncableEntity, number>,
    records: SyncChangeRecord[],
  ): Promise<void> {
    await db.transaction('rw', table, async () => {
      for (const record of records) {
        // uuid first, then serverId. The fallback is not belt-and-braces: the
        // two identities can legitimately disagree. A record the server already
        // held under its own uuid - saved online, or derived - keeps that uuid
        // when a client's create is applied to it, so matching on uuid alone
        // would miss the local row and insert a duplicate on every pull.
        const existing =
          (record.uuid
            ? await table.where('uuid').equals(record.uuid).first()
            : undefined) ??
          (await table.where('serverId').equals(record.serverId).first());

        if (existing && existing.syncStatus !== 'synced') continue;

        if (record.deleted) {
          if (existing) await table.delete(existing.localId as number);
          continue;
        }

        await table.put({
          ...record.data,
          localId: existing?.localId,
          uuid: record.uuid ?? existing?.uuid ?? `srv-${record.serverId}`,
          serverId: record.serverId,
          version: record.version,
          createdAt: existing?.createdAt ?? record.updatedAt,
          updatedAt: record.updatedAt,
          syncStatus: 'synced',
          deletedAt: null,
        });
      }
    });
  }

  // ── Conflict resolution ──────────────────────────────────────────────────

  /**
   * Settle a conflict.
   *
   *   keepServer - discard the local edit and take the server's row. Safe: the
   *                local version is preserved in the conflict record.
   *   keepLocal  - re-send the local edit, rebased onto the server's current
   *                version so it is accepted rather than rejected again. This
   *                deliberately overwrites the other user's change, which is
   *                why it must be an explicit choice and never a default.
   *   merged     - caller supplies the reconciled record, also rebased.
   *
   * Rebasing (adopting `serverVersion` as the new base) is what turns a
   * decision into an accepted write. Without it the retry would collide with
   * the same version and conflict forever.
   */
  async resolveConflict(
    entity: EntityName,
    localId: number,
    resolution: ConflictResolution,
    mergedPayload?: Record<string, unknown>,
  ): Promise<void> {
    const db = await this.databaseService.ready();
    const conflict = await this.queue.getConflictFor(entity, localId);
    if (!conflict) return;

    const table = db.table<SyncableEntity, number>(describe(entity).table);

    await db.transaction('rw', table, db.syncQueue, db.conflicts, async () => {
      const row = await table.get(localId);
      if (!row) return;

      if (resolution === 'keepServer') {
        await table.put({
          ...(conflict.serverSnapshot as object),
          localId,
          uuid: row.uuid,
          serverId: conflict.serverId,
          version: conflict.serverVersion,
          createdAt: row.createdAt,
          updatedAt: Date.now(),
          syncStatus: 'synced',
          deletedAt: null,
        } as SyncableEntity);
        return;
      }

      const body =
        resolution === 'merged' && mergedPayload
          ? mergedPayload
          : (conflict.localSnapshot as Record<string, unknown>);

      const rebased = {
        ...(body as object),
        localId,
        uuid: row.uuid,
        serverId: conflict.serverId,
        version: conflict.serverVersion,
        createdAt: row.createdAt,
        updatedAt: Date.now(),
        syncStatus: 'pending',
        deletedAt: null,
      } as SyncableEntity;

      await table.put(rebased);
      await db.syncQueue.add({
        opId: `${conflict.uuid}-resolve-${Date.now()}`,
        entity,
        operation: 'update',
        localId,
        uuid: row.uuid,
        serverId: conflict.serverId,
        payload: this.stripLocalFields(rebased),
        baseVersion: conflict.serverVersion,
        status: 'pending',
        attempts: 0,
        nextAttemptAt: Date.now(),
        lastError: null,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      });
    });

    await this.queue.closeConflict(conflict.id as number, resolution);
    await this.refreshCounts();
    void this.sync('manual');
  }

  private stripLocalFields(record: SyncableEntity): Record<string, unknown> {
    const { localId: _l, syncStatus: _s, ...rest } = record as Record<string, unknown> &
      SyncableEntity;
    void _l;
    void _s;
    return rest as Record<string, unknown>;
  }

  // ── Status ───────────────────────────────────────────────────────────────

  /** Recount what the badge shows. */
  async refreshCounts(): Promise<void> {
    if (!this.databaseService.isAvailable) return;
    try {
      const [pendingCount, failedCount, conflictCount, lastSyncedAt] =
        await Promise.all([
          this.queue.countPending(),
          this.queue.countFailed(),
          this.queue.countOpenConflicts(),
          this.queue.getLastSyncedAt(),
        ]);
      this.patch({ pendingCount, failedCount, conflictCount, lastSyncedAt });
    } catch {
      // Counting must never break the app; the badge simply keeps its
      // previous numbers.
    }
  }

  private patch(partial: Partial<SyncState>): void {
    this.stateSignal.update((s) => ({ ...s, ...partial }));
  }

  /** Entities whose local edits are pushed - used by diagnostics screens. */
  get pushableEntities(): readonly EntityName[] {
    return PUSHABLE_ENTITIES;
  }

  /**
   * Why sync is, or is not, happening - in one call.
   *
   * "It did not sync" has several very different causes: no session yet, no
   * network, the database never opened, a permanent server rejection. From the
   * outside they look identical. Reporting all of them together turns a bisect
   * into one console call.
   *
   * Exposed as `__syncDiagnostics()` in development - see offline.providers.ts.
   */
  async diagnose(): Promise<Record<string, unknown>> {
    const database = this.databaseService;
    const counts: Record<string, number> = {};
    let cursors: Record<string, unknown> = {};

    if (database.isAvailable && !database.fatalError) {
      try {
        const db = await database.ready();
        for (const entity of PULLABLE_ENTITIES) {
          cursors[entity] = await this.queue.getCursor(entity);
          counts[entity] = await db.table(describe(entity).table).count();
        }
      } catch (error) {
        cursors = { error: String(error) };
      }
    }

    return {
      databaseAvailable: database.isAvailable,
      databaseError: database.fatalError?.message ?? null,
      authenticated: this.api.hasAuthToken(),
      online: this.connectivity.isOnline(),
      started: this.started,
      running: this.running,
      state: this.stateSignal(),
      rowCounts: counts,
      pullCursors: cursors,
    };
  }
}
